import L from 'leaflet';
import { Batiment } from '../../../shared/types/game';

/** Aligné sur WorldMap — France */
export const DEFAULT_MAP_CENTER: [number, number] = [46.6034, 1.8883];
export const DEFAULT_MAP_ZOOM = 6;
export const MAP_MIN_ZOOM = 3;
export const MAP_MAX_ZOOM = 18;

export const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const OSM_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const MAP_STATE_KEY = 'mapState';

export function readStoredMapView(): { center: [number, number]; zoom: number } {
  let center: [number, number] = [...DEFAULT_MAP_CENTER] as [number, number];
  let zoom = DEFAULT_MAP_ZOOM;

  try {
    const raw = localStorage.getItem(MAP_STATE_KEY);
    if (!raw) return { center, zoom };
    const parsed = JSON.parse(raw);
    if (parsed.center && Array.isArray(parsed.center) && parsed.center.length === 2) {
      center = [Number(parsed.center[0]), Number(parsed.center[1])];
    }
    if (typeof parsed.zoom === 'number' && parsed.zoom >= MAP_MIN_ZOOM && parsed.zoom <= MAP_MAX_ZOOM) {
      zoom = parsed.zoom;
    }
  } catch {
    /* ignore */
  }
  return { center, zoom };
}

export function getBuildingIconPath(buildingType: string): string {
  switch (buildingType) {
    case 'pompier':
      return '/images/feu.png';
    case 'hopital':
      return '/images/crois.png';
    case 'fdo':
    case 'police':
      return '/images/forces-de-lordre.png';
    case 'prison':
      return '/images/prison.png';
    case 'cta':
      return '/images/antenne-cta.png';
    default:
      return '/images/icon-bat.png';
  }
}

export function getBuildingIconConfig(buildingType: string): { backgroundColor: string; iconColor: string } {
  switch (buildingType) {
    case 'pompier':
      return { backgroundColor: '#ef4444', iconColor: '#ffffff' };
    case 'hopital':
      return { backgroundColor: '#ffffff', iconColor: '#ffffff' };
    case 'fdo':
    case 'police':
      return { backgroundColor: '#1e40af', iconColor: '#ffffff' };
    case 'prison':
      return { backgroundColor: '#ffffff', iconColor: '#ffffff' };
    case 'cta':
      return { backgroundColor: '#eab308', iconColor: '#ffffff' };
    default:
      return { backgroundColor: '#10b981', iconColor: '#ffffff' };
  }
}

/**
 * Coordonnées carte + indicateur si la position vient bien d'une localisation parsée
 * (utilisé pour les cercles « secteur interventions » sur WorldMap).
 */
export function getBuildingLatLng(building: Batiment): {
  lat: number;
  lng: number;
  coordsFromLocalisation: boolean;
} {
  const defLat = DEFAULT_MAP_CENTER[0];
  const defLng = DEFAULT_MAP_CENTER[1];
  let lat = defLat;
  let lng = defLng;
  let coordsFromLocalisation = false;

  if (!building.localisation) {
    return { lat, lng, coordsFromLocalisation: false };
  }

  try {
    const loc = building.localisation;
    if (typeof loc === 'string' && (loc.startsWith('[') || loc.startsWith('{'))) {
      const parsed: unknown = JSON.parse(loc);
      if (Array.isArray(parsed) && parsed.length === 2) {
        lat = parseFloat(String(parsed[0]));
        lng = parseFloat(String(parsed[1]));
        coordsFromLocalisation = Number.isFinite(lat) && Number.isFinite(lng);
      } else if (parsed && typeof parsed === 'object' && parsed !== null) {
        const o = parsed as { lat?: unknown; lng?: unknown };
        if (o.lat !== undefined && o.lng !== undefined) {
          lat = parseFloat(String(o.lat));
          lng = parseFloat(String(o.lng));
          coordsFromLocalisation = Number.isFinite(lat) && Number.isFinite(lng);
        }
      }
    } else if (typeof loc === 'string') {
      const coords = loc.split(',');
      if (coords.length === 2) {
        const parsedLat = parseFloat(coords[0].trim());
        const parsedLng = parseFloat(coords[1].trim());
        if (!Number.isNaN(parsedLat) && !Number.isNaN(parsedLng)) {
          lat = parsedLat;
          lng = parsedLng;
          coordsFromLocalisation = true;
        }
      }
    } else if (Array.isArray(loc)) {
      const locArray = loc as unknown[];
      if (locArray.length === 2) {
        lat = parseFloat(String(locArray[0]));
        lng = parseFloat(String(locArray[1]));
        coordsFromLocalisation = Number.isFinite(lat) && Number.isFinite(lng);
      }
    }
  } catch {
    /* garde défaut */
  }

  if (!coordsFromLocalisation) {
    lat = defLat;
    lng = defLng;
  }

  return { lat, lng, coordsFromLocalisation };
}

export function createBuildingDivIcon(building: Batiment): L.DivIcon {
  const iconConfig = getBuildingIconConfig(building.type);
  const iconPath = getBuildingIconPath(building.type);
  return L.divIcon({
    className: `building-marker building-marker-${building.type}`,
    html: `
          <div style="
            background-color: ${iconConfig.backgroundColor};
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
          ">
            <img 
              src="${iconPath}" 
              alt="${building.type}"
              style="
                width: 32px;
                height: 32px;
                object-fit: contain;
              "
            />
          </div>
        `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}
